import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { oauthAPI, accountsAPI, subscriptionAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  PhotoIcon,
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  UserIcon,
  LinkIcon,
  CurrencyDollarIcon,
  GiftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Footer from './Footer';
import SubscriptionSettings from './SubscriptionSettings';
import Referrals from './Referrals';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackOAuth, trackError } from '../utils/analytics';

const Settings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(t('settings.tabs.connectedAccounts'));
  
  // Check for tab parameter in URL and set active tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'subscription') {
      setActiveTab(t('settings.tabs.subscription'));
    } else if (tabParam === 'profile') {
      setActiveTab(t('settings.tabs.profile'));
    }
    
    // Check if user just upgraded (returned from Polar checkout)
    const upgraded = searchParams.get('upgraded');
    const customerSessionToken = searchParams.get('customer_session_token');
    if (upgraded === 'true' && customerSessionToken) {
      // User just completed checkout, check subscription status
      toast.loading('Updating subscription status...');
      
      subscriptionAPI.checkStatus()
        .then(response => {
          toast.dismiss();
          if (response.data.subscription) {
            toast.success(`Successfully upgraded to ${response.data.subscription.tier} plan!`);
            // Refresh user data to get updated subscription info
            window.location.reload();
          } else {
            toast.success('Payment processed! Your subscription will be active shortly.');
          }
        })
        .catch(error => {
          toast.dismiss();
          console.error('Error checking subscription:', error);
          toast.error('Unable to verify subscription status. Please refresh the page in a moment.');
        });
        
      // Clean up URL parameters
      searchParams.delete('upgraded');
      searchParams.delete('customer_session_token');
      navigate(`/settings?${searchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);
  const [showInstagramOptions, setShowInstagramOptions] = useState(false);
  const [showGoogleInstructions, setShowGoogleInstructions] = useState(false);
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [showGoogleDriveRequiredModal, setShowGoogleDriveRequiredModal] = useState(false);

  // Fetch OAuth status
  const { data: oauthStatus, refetch } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: () => oauthAPI.getStatus().then(res => res.data),
    enabled: !!user,
  });

  // Fetch Instagram accounts for multi-account users
  const { data: accountsData } = useQuery({
    queryKey: ['instagram-accounts'],
    queryFn: () => accountsAPI.getInstagramAccounts().then(res => res.data),
    enabled: !!user && (user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency'),
  });

  const tabs = [
    { name: t('settings.tabs.connectedAccounts'), icon: LinkIcon },
    { name: t('settings.tabs.subscription'), icon: CurrencyDollarIcon },
    { name: t('settings.tabs.referrals'), icon: GiftIcon },
    { name: t('settings.tabs.profile'), icon: UserIcon },
  ];

  const handleConnectInstagram = async (accountType = 'business') => {
    try {
      const response = await oauthAPI.getInstagramAuthUrl(accountType);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect Instagram');
    }
  };

  // Handle "I have a personal account" - show info modal about switching to Creator
  const handlePersonalAccountHelp = async () => {
    setShowInstagramOptions(false);
    // Log this click for analytics
    try {
      await oauthAPI.logPersonalAccountClick();
    } catch (e) {
      // Ignore errors - logging shouldn't block
    }
    setShowPersonalInfoModal(true);
  };

  // Proceed to Instagram OAuth after user acknowledges they need to switch to Creator
  const handlePersonalAccountContinue = () => {
    setShowPersonalInfoModal(false);
    handleConnectInstagram('business'); // Uses same OAuth flow
  };

  const handleConnectGoogle = async () => {
    setShowGoogleInstructions(true);
  };

  const proceedWithGoogleConnection = async () => {
    try {
      const response = await oauthAPI.getGoogleAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect Google Drive');
    }
  };

  const handleDisconnectInstagram = async (accountId) => {
    const message = accountId 
      ? t('settings.instagram.disconnectConfirmAccount')
      : t('settings.instagram.disconnectConfirm');
      
    if (!window.confirm(message)) {
      return;
    }
    
    try {
      if (accountId) {
        await accountsAPI.disconnectInstagramAccount(accountId);
        // Track Instagram account removal
        trackOAuth.removeInstagramAccount(accountId);
        toast.success(t('settings.instagram.disconnectedAccount'));
        // Refetch both queries
        refetch();
        if (accountsData) {
          // Refetch accounts data if it exists
          window.location.reload(); // Simple reload to refresh all data
        }
      } else {
        await oauthAPI.disconnectInstagram();
        // Track Instagram disconnection
        trackOAuth.disconnectInstagram();
        toast.success(t('settings.instagram.disconnected'));
        refetch();
      }
    } catch (error) {
      // Track error
      trackError.authError('instagram_disconnect_failed', error.message || t('settings.instagram.failed'));
      toast.error(t('settings.instagram.failed'));
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm(t('settings.googleDrive.disconnectConfirm'))) {
      return;
    }
    
    try {
      await oauthAPI.disconnectGoogle();
      // Track Google Drive disconnection
      trackOAuth.disconnectGoogleDrive();
      toast.success(t('settings.googleDrive.disconnected'));
      refetch();
    } catch (error) {
      // Track error
      trackError.authError('google_disconnect_failed', error.message || t('settings.googleDrive.failed'));
      toast.error(t('settings.googleDrive.failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-2 sm:mb-4">{t('settings.title')}</h1>
          <p className="text-sm sm:text-lg text-gray-300 max-w-2xl mx-auto">
            {t('settings.subtitle')}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-1.5 sm:p-2 mb-6 sm:mb-8 border border-white/20 shadow-2xl overflow-x-auto">
          <nav className="flex space-x-1 min-w-max sm:min-w-0" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`${
                  activeTab === tab.name
                    ? 'bg-white/20 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                } group relative min-w-0 flex-1 overflow-hidden rounded-xl sm:rounded-2xl py-2.5 sm:py-4 px-3 sm:px-6 text-center text-xs sm:text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-500 whitespace-nowrap`}
              >
                <div className="flex items-center justify-center">
                  <tab.icon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                  <span className="hidden sm:inline">{tab.name}</span>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 shadow-2xl">
          {activeTab === t('settings.tabs.connectedAccounts') && (
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">{t('settings.tabs.connectedAccounts')}</h3>
                <p className="text-gray-300 mb-8">
                  {t('settings.connectedAccounts.description')}
                </p>
              </div>

              <div className="grid gap-4 sm:gap-6">
                {/* Instagram Connection */}
                <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                          <PhotoIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <h4 className="text-base sm:text-lg font-semibold text-white">Instagram</h4>
                        <p className="text-xs sm:text-sm text-gray-300">
                          {accountsData?.accounts?.length > 0 || oauthStatus?.instagram_connected
                            ? t('settings.instagram.accountsConnected', { count: accountsData?.accounts?.length || 1 })
                            : t('settings.instagram.connectDescription')}
                        </p>
                        {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency') && accountsData && (
                          <p className="text-xs text-gray-400 mt-1">
                            {accountsData.accounts.length} of {accountsData.limit} accounts used
                          </p>
                        )}
                      </div>
                    </div>
                    {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency') && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/dashboard');
                        }}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 border border-white/20 text-sm font-medium rounded-xl text-white hover:bg-white/10 transition-colors"
                      >
                        Manage Accounts
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* List of connected accounts */}
                  {(accountsData?.accounts?.length > 0 || (oauthStatus?.instagram_connected && oauthStatus?.instagram_username)) && (
                    <div className="space-y-2 mt-4">
                      {accountsData?.accounts ? (
                        accountsData.accounts.map((account) => (
                          <div key={account.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                            <div className="flex items-center">
                              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                              <div>
                                <p className="text-sm font-medium text-white">@{account.username}</p>
                                <p className="text-xs text-gray-400">
                                  {account.synced_posts_count || 0} posts synced
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDisconnectInstagram(account.id);
                              }}
                              className="text-xs text-red-400 hover:text-red-300 transition-colors"
                            >
                              {t('common.disconnect')}
                            </button>
                          </div>
                        ))
                      ) : oauthStatus?.instagram_username && (
                        <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                          <div className="flex items-center">
                            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                            <p className="text-sm font-medium text-white">@{oauthStatus.instagram_username}</p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDisconnectInstagram();
                            }}
                            className="text-xs text-red-400 hover:text-red-300 transition-colors"
                          >
                            {t('common.disconnect')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Connect button for FREE/PREMIUM users */}
                  {user?.subscription_tier !== 'pro' && user?.subscription_tier !== 'agency' && !oauthStatus?.instagram_connected && (
                    <div className="mt-4">
                      {!showInstagramOptions ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            // Check if Google Drive is connected first
                            if (!oauthStatus?.google_connected) {
                              setShowGoogleDriveRequiredModal(true);
                              return;
                            }
                            setShowInstagramOptions(true);
                          }}
                          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-colors shadow-lg"
                        >
                          {t('settings.instagram.connect')}
                          <ArrowRightIcon className="ml-2 h-4 w-4" />
                        </button>
                      ) : (
                        <div className="space-y-2 p-3 bg-white/5 rounded-xl">
                          <p className="text-xs text-gray-400 mb-2">Select your account type:</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleConnectInstagram('business');
                      setShowInstagramOptions(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="font-medium text-white">Creator/Business Account</div>
                    <div className="text-xs text-gray-400">Recommended - Full access to save posts, stories, and reels</div>
                  </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePersonalAccountHelp();
                            }}
                            className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-white/10 transition-colors"
                          >
                            <div className="font-medium text-white">Personal Account</div>
                            <div className="text-xs text-gray-400">I'll switch to Creator (it's free!)</div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowInstagramOptions(false);
                            }}
                            className="w-full text-center px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Google Drive Connection */}
                <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`h-10 w-10 sm:h-12 sm:w-12 ${oauthStatus?.google_connection_error ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-blue-500 to-green-500'} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg`}>
                          <CloudIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <h4 className="text-base sm:text-lg font-semibold text-white">Google Drive</h4>
                        <p className="text-xs sm:text-sm text-gray-300">
                          {oauthStatus?.google_connection_error
                            ? 'Permission issue - please reconnect'
                            : oauthStatus?.google_connected
                              ? `Connected as ${oauthStatus.google_email || 'user'}`
                              : 'Connect your Google Drive to store your backups'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:space-x-3">
                      {oauthStatus?.google_connected ? (
                        <>
                          {!oauthStatus?.google_connection_error && (
                            <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 hidden sm:block" />
                          )}
                          {oauthStatus?.google_connection_error && (
                            <XCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-400 hidden sm:block" />
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDisconnectGoogle();
                            }}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 border border-red-500 text-sm font-medium rounded-xl text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                          >
                            {t('common.disconnect')}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleConnectGoogle();
                          }}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 transition-colors shadow-lg"
                        >
                          {t('common.connect')}
                          <ArrowRightIcon className="ml-2 h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Connection Error Warning */}
                  {oauthStatus?.google_connection_error && (
                    <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                      <p className="text-sm text-red-300 font-medium mb-2">
                        ⚠️ Google Drive permission issue detected
                      </p>
                      <p className="text-xs text-gray-300 mb-3">
                        Your backups are failing because the Google Drive connection doesn't have the required permissions. 
                        When reconnecting, make sure to check this box:
                      </p>
                      <img 
                        src="/google-drive-permission.png" 
                        alt="Google Drive permission checkbox" 
                        className="w-full rounded-lg border border-white/20 shadow-lg mb-3"
                      />
                      <p className="text-xs text-white font-medium">
                        To fix: Click "Disconnect" above, then reconnect using a regular browser (Chrome, Safari, etc.) and check the box shown above.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === t('settings.tabs.subscription') && (
            <SubscriptionSettings />
          )}

          {activeTab === t('settings.tabs.referrals') && (
            <Referrals />
          )}

          {activeTab === t('settings.tabs.profile') && (
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">{t('settings.tabs.profile')}</h3>
                <p className="text-gray-300 mb-8">
                  {t('settings.profile.description')}
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <UserIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-white">Account Details</h4>
                      <p className="text-sm text-gray-300">
                        {user?.email || 'No email available'}
                      </p>
                      <p className="text-sm text-gray-300">
                        {user?.full_name || 'No name available'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <h4 className="text-lg font-semibold text-white mb-4">{t('settings.profile.accountSettings')}</h4>
                  <p className="text-sm text-gray-300">
                    Additional account management features will be available here soon.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer variant="dark" />

      {/* Google Drive Required Modal - Shows when user tries to connect Instagram without Google Drive */}
      {showGoogleDriveRequiredModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-gray-900 rounded-3xl shadow-2xl max-w-md w-full p-8 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-white">
                {t('dashboard.googleDriveRequired.title')}
              </h3>
              <button
                onClick={() => setShowGoogleDriveRequiredModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-5">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 via-green-400 to-yellow-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-4">
                <img src="/google_drive_icon_.png" alt="Google Drive" className="h-8 w-8" />
              </div>
              
              <p className="text-white text-center text-lg font-medium mb-3">
                {t('dashboard.googleDriveRequired.heading')}
              </p>
              
              <p className="text-gray-300 text-center text-sm mb-4">
                {t('dashboard.googleDriveRequired.description')}
              </p>
            </div>

            <div className="mb-5 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <div className="flex items-start space-x-3">
                <CloudIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-200 text-sm font-medium mb-1">
                    {t('dashboard.googleDriveRequired.whyTitle')}
                  </p>
                  <p className="text-gray-300 text-sm">
                    {t('dashboard.googleDriveRequired.whyDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowGoogleDriveRequiredModal(false)}
                className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowGoogleDriveRequiredModal(false);
                  handleConnectGoogle();
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl hover:from-blue-600 hover:to-green-600 transition-colors font-medium shadow-lg"
              >
                {t('dashboard.googleDriveRequired.connectButton')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Google Drive Instructions Modal */}
      {showGoogleInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-gray-900 rounded-3xl shadow-2xl max-w-md w-full p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">Connect Google Drive</h3>
            
            <div className="space-y-4 mb-6">
              <p className="text-gray-300">
                Before connecting your Google Drive, please note:
              </p>
              
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <p className="text-sm text-orange-300 font-medium mb-3">
                  ⚠️ Important: When Google asks for permissions, make sure this checkbox is checked:
                </p>
                <img 
                  src="/google-drive-permission.png" 
                  alt="Google Drive permission checkbox" 
                  className="w-full rounded-lg border border-white/20 shadow-lg"
                />
              </div>
              
              <p className="text-gray-300 text-sm">
                This allows Vaulto to create a dedicated folder for your Instagram backups without accessing your other files.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowGoogleInstructions(false);
                }}
                className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowGoogleInstructions(false);
                  proceedWithGoogleConnection();
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl hover:from-blue-600 hover:to-green-600 transition-colors font-medium shadow-lg"
              >
                Continue to Google
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Personal Account Info Modal - Switch to Creator */}
      {showPersonalInfoModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/80 backdrop-blur-sm" onClick={() => setShowPersonalInfoModal(false)} />
            
            <div className="relative inline-block w-full max-w-lg p-6 overflow-hidden text-left align-middle transition-all transform bg-white/10 backdrop-blur-lg border border-white/20 shadow-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowPersonalInfoModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  Join Millions of Creators
                </h3>
                <p className="text-sm text-gray-300">
                  Top influencers and serious content creators use Creator accounts for powerful insights and tools. Upgrade in 30 seconds - it's completely free!
                </p>
              </div>

              {/* Step-by-step guide with screenshots */}
              <div className="space-y-4 mb-6">
                {/* Step 1 */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm font-medium text-white mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">1</span>
                    Instagram will ask you to switch to Professional
                  </p>
                  <img 
                    src="/instagram_switch_to_professional.jpeg" 
                    alt="Instagram switch to professional prompt" 
                    className="w-full max-w-xs mx-auto rounded-lg border border-white/20 shadow-lg"
                  />
                </div>

                {/* Step 2 */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm font-medium text-white mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">2</span>
                    Select "Creator" when asked
                  </p>
                  <div className="relative w-full max-w-xs mx-auto">
                    <img 
                      src="/instagram_creator_or_business.jpeg" 
                      alt="Instagram creator or business selection" 
                      className="w-full rounded-lg border border-white/20 shadow-lg"
                    />
                    {/* Green indicator circle on Creator option */}
                    <div className="absolute top-[28%] right-[8%] w-8 h-8 border-4 border-green-400 rounded-full animate-pulse"></div>
                    <div className="absolute top-[28%] right-[8%] w-8 h-8 flex items-center justify-center">
                      <CheckCircleIcon className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info text */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-200">
                  <strong>Why Creators choose this:</strong> Access to analytics and insights, professional tools, and the ability to automatically backup your content with Vaulto. Plus, you can switch back anytime - your followers and content stay exactly the same.
                </p>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <button
                  onClick={handlePersonalAccountContinue}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all"
                >
                  Continue to Instagram
                </button>

                <button
                  onClick={() => setShowPersonalInfoModal(false)}
                  className="w-full px-4 py-3 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;
